import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, Loader2, Download } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { SheetMapper } from "@/components/SheetMapper";

const TEMPLATE = `date,client,campaign,manager,via,spend,revenue,currency,validation_status,validation_due,payment_due,payment_status,notes
2026-06-01,Nova Retail,Search - Brand,Rahul Verma,Direct MCC,25000,34000,INR,validated,2026-06-04,2026-06-16,paid,
2026-06-01,Aurora Fintech,PMax - Retail,Sneha Iyer,Agency Reseller,41000,38000,INR,pending,2026-06-04,2026-06-16,unpaid,check creatives
`;

export default function Sheets() {
  const { reloadMasters } = useData();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [preview, setPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [mapping, setMapping] = useState({});
  const [presetName, setPresetName] = useState("");

  const loadHistory = () => api.get("/upload/history").then((r) => setHistory(r.data)).catch(() => {});

  useEffect(() => {
    loadHistory();
  }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      setPendingFile(file);
      const unmapped = ["date", "client", "campaign"].filter((f) => !data.suggested_mapping?.[f]);
      if (unmapped.length) toast.info(`Please map: ${unmapped.join(", ")}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    const missing = ["date", "client", "campaign"].filter((f) => !mapping[f]);
    if (missing.length) {
      toast.error(`Map these first: ${missing.join(", ")}`);
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", pendingFile);
    fd.append("mapping", JSON.stringify(mapping));
    if (presetName.trim()) fd.append("save_mapping_as", presetName.trim());
    try {
      const { data } = await api.post("/upload/sheet", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setPreview(null);
      setPendingFile(null);
      setPresetName("");
      toast.success(`${data.inserted} added, ${data.updated} updated`);
      await reloadMasters();
      loadHistory();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "ref-revenue-daily-template.csv";
    a.click();
  };

  return (
    <div data-testid="sheets-page">
      <div className="mb-8">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
          Data Pipeline
        </div>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">Sheet Sync</h1>
        <p className="mt-4 max-w-2xl text-sm text-slate-600">
          Export your SharePoint / Excel daily reporting sheet and drop it here. Rows are merged by
          date + client + campaign + via, so re-uploading an updated sheet never creates duplicates.
          Unknown clients, campaigns, managers and vias are created automatically.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div
            className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 bg-slate-50 p-14 text-center transition-colors hover:border-slate-900"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              upload(e.dataTransfer.files?.[0]);
            }}
            data-testid="upload-dropzone"
          >
            {busy ? (
              <Loader2 className="h-10 w-10 animate-spin text-slate-900" />
            ) : (
              <UploadCloud className="h-10 w-10 text-slate-400" />
            )}
            <div className="mt-4 font-display text-lg font-bold tracking-tight">
              Drop CSV or Excel file here
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Your own column names are fine — you'll map them in the next step
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              data-testid="upload-file-input"
              onChange={(e) => upload(e.target.files?.[0])}
            />
            <div className="mt-6 flex gap-2">
              <Button
                data-testid="browse-file-button"
                className="rounded-md bg-slate-900 hover:bg-slate-800 active:scale-95"
                onClick={() => inputRef.current?.click()}
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" /> Browse file
              </Button>
              <Button
                variant="outline"
                data-testid="download-template-button"
                className="rounded-md border-slate-300"
                onClick={downloadTemplate}
              >
                <Download className="mr-2 h-4 w-4" /> Template
              </Button>
            </div>
          </div>

          {preview && (
            <SheetMapper
              preview={preview}
              mapping={mapping}
              setMapping={setMapping}
              presetName={presetName}
              setPresetName={setPresetName}
              busy={busy}
              onCancel={() => {
                setPreview(null);
                setPendingFile(null);
              }}
              onImport={commit}
            />
          )}

          {result && (            <div className="mt-6 border border-slate-200 bg-white p-5" data-testid="upload-result">
              <div className="font-display text-base font-bold">Last import</div>
              <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Rows</div>
                  <div className="text-xl font-bold">{result.rows}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Inserted</div>
                  <div className="text-xl font-bold text-emerald-600">{result.inserted}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Updated</div>
                  <div className="text-xl font-bold text-slate-900">{result.updated}</div>
                </div>
              </div>
              {result.errors?.length > 0 && (
                <ul className="mt-4 space-y-1 text-xs text-red-600">
                  {result.errors.map((er, i) => (
                    <li key={i}>{er}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-5 py-3 font-display text-base font-bold">
            Import History
          </div>
          <div className="divide-y divide-slate-100" data-testid="upload-history">
            {history.length === 0 && <div className="p-5 text-sm text-slate-500">No imports yet.</div>}
            {history.map((h) => (
              <div key={h.id} className="px-5 py-3 text-sm">
                <div className="truncate font-semibold">{h.filename}</div>
                <div className="text-xs text-slate-500">
                  {new Date(h.at).toLocaleString()} · {h.inserted} new / {h.updated} updated
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
